import { supabase } from './supabase';

// Lookups go through the classroom-translate edge function, which reuses the
// app's own Claude-made translation when one exists and otherwise asks Google.
// It never calls Claude itself, and never stores Google's answer server-side —
// so this page-lifetime memo is what makes a second tap instant and free.
// If the server can't get Google to answer, the browser asks Google directly (fromBrowser).

export type TranslationSource = 'gibberfish' | 'google';
export type Translation = { text: string; from: TranslationSource };

const memo = new Map<string, Translation>();

export async function translate(text: string, source: string, target: string): Promise<Translation> {
  const clean = text.replace(/\s+/g, ' ').trim();
  const key = `${source}|${target}|${clean.includes(' ') ? clean : clean.toLowerCase()}`;
  const hit = memo.get(key);
  if (hit) return hit;

  const { data, error } = await supabase.functions.invoke('classroom-translate', {
    body: { text: clean, source, target },
  });
  if (error) {
    // invoke wraps non-2xx in FunctionsHttpError; the body carries our message.
    let message = 'Translation is unavailable right now.';
    let status = 0;
    try {
      const res = (error as { context?: Response }).context;
      status = res?.status ?? 0;
      const body = await res?.json();
      if (body?.error) message = body.error;
    } catch { /* keep the generic message */ }
    // The server couldn't reach Google (its key was refused and Google rate-limits the server's shared
    // address). Ask Google's keyless endpoint from this browser instead: each student has their own quota.
    if (status === 502 || status === 0) {
      try {
        const out: Translation = { text: await fromBrowser(clean, source, target), from: 'google' };
        memo.set(key, out);
        return out;
      } catch { /* fall through to the message */ }
    }
    throw new Error(message);
  }
  const out: Translation = {
    text: String(data?.translation ?? ''),
    from: data?.from === 'gibberfish' ? 'gibberfish' : 'google',
  };
  if (out.text) memo.set(key, out);                    // never remember a blank answer
  return out;
}

/** Google's keyless endpoint, called from the browser (it allows that). Nothing is stored. */
async function fromBrowser(text: string, source: string, target: string): Promise<string> {
  const code = (c: string) => (c === 'ar-EG' ? 'ar' : c);
  const url = 'https://translate.googleapis.com/translate_a/single?client=gtx&dt=t'
    + `&sl=${encodeURIComponent(code(source))}&tl=${encodeURIComponent(code(target))}&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`gtx ${res.status}`);
  const data = await res.json();
  const parts: unknown[] = Array.isArray(data?.[0]) ? data[0] : [];
  const out = parts.map(p => (Array.isArray(p) ? String(p[0] ?? '') : '')).join('').trim();
  if (!out) throw new Error('no translation');
  return out;
}
